import { usePageMeta } from "@/lib/use-page-meta";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { ContactDialog } from "@/components/contact-dialog";
import { SuggestYoutuberDialog } from "@/components/suggest-youtuber-dialog";
import { lastUpdateQuery, siteStatsQuery } from "@/lib/queries";
import { formatDate, formatNumber } from "@/lib/format";
import { safeLoadAll } from "@/lib/ssr-safe";
import "@/i18n";

export const Route = createFileRoute("/about")({
  staticData: { sitemap: true },
  head: () => ({
    meta: [
      { title: "El projecte · Youtubers Map" },
      {
        name: "description",
        content:
          "Com funciona Youtubers Map: d'on surten les dades, com es detecten les localitzacions dels vídeos, com se seleccionen els youtubers, cada quan s'actualitza i quines limitacions té.",
      },
      { property: "og:title", content: "El projecte · Youtubers Map" },
      {
        property: "og:description",
        content:
          "Metodologia de Youtubers Map: fonts de dades, geolocalització amb IA, selecció de canals, freqüència d'actualització i limitacions.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  loader: ({ context }) => {
    // Both reads are instant database functions, so the server waits for them
    // and the figures reach crawlers in the first HTML instead of only after
    // the browser has downloaded the archive.
    return safeLoadAll([
      ["about:lastUpdate", context.queryClient.ensureQueryData(lastUpdateQuery)],
      ["about:siteStats", context.queryClient.ensureQueryData(siteStatsQuery)],
    ]);
  },
  component: AboutPage,
});

function Section({ title, body }: { title: string; body: string }) {
  return (
    <section className="border-t border-ink/8 pt-6">
      <h2 className="text-xl md:text-2xl mb-3">{title}</h2>
      <p className="text-sm md:text-base leading-relaxed text-ink/70">{body}</p>
    </section>
  );
}

function AboutPage() {
  const { t, i18n } = useTranslation();
  usePageMeta(t("meta.aboutTitle"), t("meta.aboutDesc"));
  const { data: lastUpdate } = useQuery(lastUpdateQuery);
  const { data: stats } = useQuery(siteStatsQuery);

  return (
    <div className="min-h-screen">
      <SiteNav />
      <div className="max-w-3xl mx-auto px-6 py-10 md:py-14">
        <p className="text-mono-caps text-amber mb-2">{t("about.kicker")}</p>
        <h1 className="text-3xl md:text-5xl mb-5">{t("about.title")}</h1>
        <p className="text-base md:text-lg leading-relaxed text-ink/75 mb-4">{t("about.intro")}</p>
        {lastUpdate && (
          <p className="text-mono-caps text-ink/50 mb-10">
            {t("footer.lastUpdate", { date: formatDate(lastUpdate, i18n.language) })}
          </p>
        )}

        {stats && (
          <section className="border-t border-ink/8 pt-6 mb-8">
            <h2 className="text-xl md:text-2xl mb-5">{t("about.statsTitle")}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: stats.videos, label: t("about.statVideos") },
                { value: stats.locations, label: t("about.statLocations") },
                { value: stats.youtubers, label: t("about.statChannels") },
                { value: stats.countries, label: t("about.statCountries") },
              ].map((s) => (
                <div key={s.label}>
                  <div className="text-3xl md:text-4xl tabular-nums">{formatNumber(s.value, i18n.language)}</div>
                  <div className="text-mono-caps text-ink/50 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="space-y-8">
          <Section title={t("about.dataTitle")} body={t("about.dataBody")} />
          <Section title={t("about.geoTitle")} body={t("about.geoBody")} />
          <Section title={t("about.selectionTitle")} body={t("about.selectionBody")} />
          <Section title={t("about.freqTitle")} body={t("about.freqBody")} />
          <Section title={t("about.limitsTitle")} body={t("about.limitsBody")} />
          <section className="border-t border-ink/8 pt-6">
            <h2 className="text-xl md:text-2xl mb-3">{t("about.contactTitle")}</h2>
            <p className="text-sm md:text-base leading-relaxed text-ink/70 mb-4">
              {t("about.contactBody")}
            </p>
            <div className="flex flex-wrap gap-3">
              <ContactDialog
                trigger={
                  <button
                    type="button"
                    className="bg-ink text-paper px-4 py-2 text-sm font-medium hover:bg-amber transition-colors"
                  >
                    {t("contact.cta")}
                  </button>
                }
              />
              <SuggestYoutuberDialog
                trigger={
                  <button
                    type="button"
                    className="border border-ink/20 px-4 py-2 text-sm hover:bg-secondary transition-colors"
                  >
                    {t("home.suggest.cta")}
                  </button>
                }
              />
            </div>
          </section>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
